import React, { forwardRef, MouseEvent, PropsWithChildren } from "react";
import { useTranslation } from "i18n";
import { FEATURE_FLAGS, useLaunchDarklyFlags } from "shared-services";
import GenericAppShell, { GenericAppShellProps } from "../GenericAppShell";
import { ThemeContextValues } from "../NavThemeContext";
import LeftHandNavItem from "../LeftHandNavItem";
import { ReactComponent as FolderIcon } from "../../../../../images/svg/icons/doc-folder.svg";
import { ReactComponent as CogIcon } from "../../../../../images/svg/icons/cog-settings.svg";
import { ReactComponent as MediaPlayerIcon } from "../../../../../images/svg/icons/media-play-skinny.svg";
import { ReactComponent as SearchIcon } from "../../../../../images/svg/icons/search.svg";
import { ReactComponent as ShortlistIcon } from "../../../../../images/svg/icons/docs-shortlist-clipboard.svg";
import { ReactComponent as PerformersIcon } from "../../../../../images/svg/icons/two-perfs.svg";
import { ReactComponent as MegaphoneIcon } from "../../../../../images/svg/icons/megaphone.svg";
import { ReactComponent as HelpIcon } from "../../../../../images/svg/icons/help-question.svg";
import { Pill } from "../../../../Atoms/Pill/Pill";
import NavItem from "../NavItem";
import NavSearch from "../NavSearch";
import QuickActionButton from "../QuickActionButton";
import { GenericAppShellRef } from "../GenericAppShell/GenericAppShell";
import {
  breakdownCreateNestedPath,
  projectFolderNestedPath,
} from "./constants";
import { checkIsSearchOnlyCD } from "shared-auth";
import CdAgentSearchAutocomplete from "./CdAgentSearchAutocomplete";
import { getCdSearchRedirect } from "./helpers/getCdSearchRedirect";
import { dataTestIds } from "data-testids";

interface CdAppShellProps extends Pick<
  GenericAppShellProps,
  "hideFooter" | "footerComponent"
> {
  createBreakdown?: (e: MouseEvent<HTMLButtonElement>) => void;
}

const searchRedirect = (searchTerm: string) =>
  window.location.replace(getCdSearchRedirect(searchTerm));

const CdAppShell = forwardRef<
  GenericAppShellRef,
  PropsWithChildren<CdAppShellProps>
>(({ createBreakdown, footerComponent, hideFooter, children }, ref) => {
  const { t } = useTranslation();
  const isSelfTapesEnabled = useLaunchDarklyFlags(FEATURE_FLAGS.SelfTapes);
  const isSearchOnlyCd = checkIsSearchOnlyCD();

  return (
    <GenericAppShell
      theme={ThemeContextValues.Cd}
      hideFooter={hideFooter}
      footerComponent={footerComponent}
      ref={ref}
      leftHandNavItems={
        <>
          {!isSearchOnlyCd && (
            <>
              <LeftHandNavItem
                to="/casting/projects"
                nestedPaths={projectFolderNestedPath}
                icon={FolderIcon}
              >
                {t("common:nav.links.projectFolders")}
              </LeftHandNavItem>
              <LeftHandNavItem
                to="/casting/create-breakdown"
                nestedPaths={breakdownCreateNestedPath}
                isSubNav
                onClick={createBreakdown}
              >
                {t("common:nav.links.createBreakdown")}
              </LeftHandNavItem>
              <LeftHandNavItem to="/casting/active-breakdowns" isSubNav>
                {t("common:nav.links.openAndDraftBreakdowns")}
              </LeftHandNavItem>
              <LeftHandNavItem to="/casting/follow-up-messages" isSubNav>
                {t("common:nav.links.followUpMessages")}
              </LeftHandNavItem>

              {isSelfTapesEnabled && (
                <LeftHandNavItem
                  to="/casting/self-tape-requests"
                  icon={MediaPlayerIcon}
                >
                  {t("common:nav.links.mediaRequests")}
                  <Pill
                    className="bg-blue-sky-2 ml-1.5"
                    theme="light"
                    size="md"
                  >
                    {t("common:nav.links.pill.freeBeta")}
                  </Pill>
                </LeftHandNavItem>
              )}
            </>
          )}

          <LeftHandNavItem to="/casting/performers" icon={SearchIcon}>
            {t("common:nav.links.performerSearch")}
          </LeftHandNavItem>

          <LeftHandNavItem to="/casting/shortlists" icon={ShortlistIcon}>
            {t("common:nav.links.shortlists")}
          </LeftHandNavItem>

          {!isSearchOnlyCd && (
            <LeftHandNavItem to="/casting/agent-lists" icon={PerformersIcon}>
              {t("common:nav.links.agentsList")}
            </LeftHandNavItem>
          )}

          <LeftHandNavItem
            to="https://www.spotlight.com/help-and-faqs/links-for-professionals-on-spotlight/"
            icon={MegaphoneIcon}
          >
            {t("common:nav.links.industryResources")}
          </LeftHandNavItem>
          <LeftHandNavItem to="https://calendar.spotlight.com" isSubNav>
            {t("common:nav.links.gradPerformanceCalendar")}
          </LeftHandNavItem>
          <LeftHandNavItem
            to="https://www.spotlight.com/news-and-advice/"
            isSubNav
          >
            {t("common:nav.links.newsAndAdvice")}
          </LeftHandNavItem>
          <LeftHandNavItem to="https://app.spotlight.com/contacts" isSubNav>
            {t("common:nav.links.contactsListings")}
          </LeftHandNavItem>
          <LeftHandNavItem
            to="https://www.spotlight.com/the-small-print/help-for-casting-users-of-the-spotlight-link/"
            isSubNav
          >
            {t("common:nav.links.helpAndTutorials")}
          </LeftHandNavItem>
          <LeftHandNavItem to="https://spotlight-showcase.com/" isSubNav>
            {t("common:nav.links.digitalShowcase")}
          </LeftHandNavItem>
          <LeftHandNavItem
            to="https://www.spotlight.com/rooms-and-studios/"
            isSubNav
          >
            {t("common:nav.links.roomAndStudiosHire")}
          </LeftHandNavItem>
        </>
      }
      dynamicHeaderContent={
        <>
          <NavSearch
            submitSearch={searchRedirect}
            renderAutoCompleteResult={(searchTerm) => (
              <CdAgentSearchAutocomplete
                searchTerm={searchTerm}
                searchRedirect={searchRedirect}
              />
            )}
          />
          <QuickActionButton
            label={t("common:nav.links.directory")}
            linkUrl="/casting/performers"
          />
        </>
      }
      userMenuItems={
        <>
          <NavItem
            to="https://portal.spotlight.com/portal/Account/Details"
            icon={CogIcon}
            data-testid={
              dataTestIds.componentLibrary[
                "Organisms.NavigationHeader.MainNavigationItem.mainNavigationLink"
              ]
            }
          >
            {t("common:nav.links.accountDetails")}
          </NavItem>
          <NavItem
            to="/myhome/account-settings/my-details"
            data-testid={
              dataTestIds.componentLibrary[
                "Organisms.NavigationHeader.SubNavigationItem.subNavigationLink"
              ]
            }
            isSubNav
          >
            {t("common:nav.links.contactDetails")}
          </NavItem>

          <NavItem
            to="https://www.spotlight.com/the-small-print/help-for-casting-users-of-the-spotlight-link/"
            icon={HelpIcon}
            data-testid={
              dataTestIds.componentLibrary[
                "Organisms.NavigationHeader.MainNavigationItem.mainNavigationLink"
              ]
            }
          >
            {t("common:nav.links.helpAndTutorials")}
          </NavItem>

          <NavItem
            to="https://support.spotlight.com/hc/en-us/"
            data-testid={
              dataTestIds.componentLibrary[
                "Organisms.NavigationHeader.SubNavigationItem.subNavigationLink"
              ]
            }
            isSubNav
          >
            {t("common:nav.links.support")}
          </NavItem>
        </>
      }
    >
      {children}
    </GenericAppShell>
  );
});

export default CdAppShell;
